import { useNavigate } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useId, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Badge, type BadgeRole } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { CONTROL, SelectField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { useIntakeSourcesQuery } from '../intake/use-intake.ts';
import { type ImportSummary, useImportsQuery, useUploadImport } from './use-imports.ts';

export const IMPORT_STATUS: Record<ImportSummary['status'], { label: string; role: BadgeRole }> = {
  uploaded: { label: 'Not started', role: 'neutral' },
  mapped: { label: 'Not started', role: 'neutral' },
  running: { label: 'Importing', role: 'pending' },
  completed: { label: 'Done', role: 'funded' },
  failed: { label: 'Stopped', role: 'declined' },
};

/** CSV imports (M8, admins): upload a file, then the history. */
export function ImportsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const imports = useImportsQuery(workspaceId);
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Imports"
        description="Add merchants from a lead vendor's file. Nothing is texted by an import."
        actions={
          <Link
            to="/merchants"
            className="fl-btn fl-btn--ghost focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          >
            Back to merchants
          </Link>
        }
      />
      <UploadCard />
      <section className="flex flex-col gap-3" aria-label="Past imports">
        <h2 className="body font-semibold text-ink">Past imports</h2>
        {imports.isPending ? <Spinner label="Loading imports" /> : null}
        {imports.isError ? <Alert tone="error">{imports.error.message}</Alert> : null}
        {imports.data?.length === 0 ? (
          <EmptyState title="No imports yet">Upload a CSV file above to add merchants.</EmptyState>
        ) : null}
        {imports.data && imports.data.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
            {imports.data.map((imp) => (
              <li key={imp.id}>
                <Link
                  to="/merchants/imports/$importId"
                  params={{ importId: imp.id }}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 hover:bg-surface focus-visible:outline-2 focus-visible:outline-focus-ring"
                  data-testid="import-row"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-ink">{imp.fileName}</span>
                    <span className="footnote text-ink-muted">
                      {new Date(imp.createdAt).toLocaleString()} ·{' '}
                      {imp.totalRows.toLocaleString('en-US')} rows
                      {imp.status === 'completed'
                        ? ` · ${imp.counts.created.toLocaleString('en-US')} new`
                        : ''}
                    </span>
                  </span>
                  <Badge role={IMPORT_STATUS[imp.status].role}>
                    {IMPORT_STATUS[imp.status].label}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

function UploadCard() {
  const { workspaceId } = useWorkspaceClaims();
  const navigate = useNavigate();
  const sources = useIntakeSourcesQuery(workspaceId);
  const upload = useUploadImport(workspaceId);
  const fileId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [sourceId, setSourceId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const vendors = (sources.data ?? []).filter((s) => s.kind === 'csv');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) {
      setError('Choose a CSV file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('The file is larger than 20 MB. Split it into smaller files.');
      return;
    }
    setError(null);
    try {
      const preview = await upload.mutateAsync({ file, sourceId: sourceId || null });
      await navigate({
        to: '/merchants/imports/$importId',
        params: { importId: preview.importId },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't upload the file.");
    }
  }

  return (
    <Card title="Import a file">
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        <p className="callout text-ink-muted">
          CSV or TSV, up to 20 MB and 50,000 rows, with a header row. From Excel, use File → Save As
          → &ldquo;CSV UTF-8&rdquo;.
        </p>
        {vendors.length > 0 ? (
          <SelectField
            label="Lead vendor (optional)"
            hint="Uses the columns you picked for this vendor last time."
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            options={[
              { value: '', label: 'No saved vendor' },
              ...vendors.map((v) => ({ value: v.id, label: v.name })),
            ]}
          />
        ) : null}
        <div className="flex flex-col gap-1">
          <label htmlFor={fileId} className="callout font-semibold text-ink">
            File
          </label>
          <input
            id={fileId}
            type="file"
            accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
            className={`${CONTROL} py-2`}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            data-testid="import-file"
          />
        </div>
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div>
          <Button type="submit" busy={upload.isPending}>
            Upload
          </Button>
        </div>
      </form>
    </Card>
  );
}
